import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import type {
  FileContent,
  FileNode,
  Session,
  SessionStatusInfo,
  TodoItem,
  VcsFileDiff,
  VcsFileStatusEntry,
  VcsInfo,
} from "../types";
import { cx } from "../util";

type TabId = "arquivos" | "git" | "diffs" | "tarefas" | "execucao";

interface RightPanelProps {
  directory: string;
  session: Session | null;
  sessionStatus?: SessionStatusInfo;
  onOpenSession: (id: string) => void;
  onWidthChange: (w: number) => void;
  /** Painel minimizado (só a linha divisória visível). */
  collapsed: boolean;
  /** Alterna minimizado/restaurado — disparado pelo duplo clique na divisória. */
  onToggleCollapse: () => void;
}

/** Renderiza um patch unificado linha a linha. */
export function DiffView({ patch }: { patch: string }) {
  const lines = (patch ?? "").split("\n");
  return (
    <div className="diff-body">
      {lines.map((line, i) => {
        const cls = line.startsWith("+")
          ? "add"
          : line.startsWith("-")
            ? "del"
            : line.startsWith("@@")
              ? "hunk"
              : "";
        return (
          <div key={i} className={cx("diff-line", cls)}>
            <span className="gutter">{cls === "add" ? "+" : cls === "del" ? "−" : ""}</span>
            <span>{line}</span>
          </div>
        );
      })}
    </div>
  );
}

function DiffFileList({ items }: { items: VcsFileDiff[] }) {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  if (items.length === 0) {
    return <p style={{ color: "var(--text-faint)", fontSize: 13 }}>Nenhuma alteração registrada.</p>;
  }
  return (
    <div>
      {items.map((d) => (
        <div className="diff-file" key={d.file}>
          <div className="diff-head">
            <button
              className="icon-btn"
              aria-label={`Expandir diff de ${d.file}`}
              onClick={() => setOpen((o) => ({ ...o, [d.file]: !o[d.file] }))}
            >
              {open[d.file] ? "▾" : "▸"}
            </button>
            <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>{d.file}</span>
            <span className="chip ok">+{d.additions}</span>
            <span className="chip err">−{d.deletions}</span>
          </div>
          {open[d.file] ? <DiffView patch={d.patch} /> : null}
        </div>
      ))}
    </div>
  );
}

export function RightPanel(props: RightPanelProps) {
  const [tab, setTab] = useState<TabId>("arquivos");
  const [treePath, setTreePath] = useState<string>("");
  const [nodes, setNodes] = useState<FileNode[]>([]);
  const [file, setFile] = useState<{ path: string; content: FileContent } | null>(null);
  const [gitInfo, setGitInfo] = useState<VcsInfo | null>(null);
  const [gitStatus, setGitStatus] = useState<VcsFileStatusEntry[]>([]);
  const [gitDiff, setGitDiff] = useState<VcsFileDiff[]>([]);
  const [sessionDiff, setSessionDiff] = useState<VcsFileDiff[]>([]);
  const [todos, setTodos] = useState<TodoItem[]>([]);
  const [children, setChildren] = useState<Session[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState<string[]>([]);

  const loadTree = useCallback(async () => {
    try {
      setError(null);
      setNodes(await api.files(props.directory, treePath || undefined));
    } catch (e) {
      setError((e as Error).message);
    }
  }, [props.directory, treePath]);

  useEffect(() => {
    if (tab === "arquivos") void loadTree();
  }, [tab, loadTree]);

  useEffect(() => {
    if (tab !== "git") return;
    void (async () => {
      try {
        setError(null);
        const [info, status, diff] = await Promise.all([
          api.vcs(props.directory),
          api.vcsStatus(props.directory),
          api.vcsDiff(props.directory),
        ]);
        setGitInfo(info);
        setGitStatus(status);
        setGitDiff(diff);
      } catch (e) {
        setError((e as Error).message);
      }
    })();
  }, [tab, props.directory]);

  useEffect(() => {
    if (tab !== "diffs" || !props.session) return;
    void api
      .sessionDiff(props.directory, props.session.id)
      .then(setSessionDiff)
      .catch((e: Error) => setError(e.message));
  }, [tab, props.directory, props.session]);

  useEffect(() => {
    if (tab !== "tarefas" || !props.session) return;
    void api
      .todos(props.directory, props.session.id)
      .then(setTodos)
      .catch((e: Error) => setError(e.message));
  }, [tab, props.directory, props.session]);

  useEffect(() => {
    if (tab !== "execucao" || !props.session) return;
    void api
      .children(props.directory, props.session.id)
      .then(setChildren)
      .catch(() => setChildren([]));
  }, [tab, props.directory, props.session]);

  async function openFile(path: string) {
    try {
      setError(null);
      const content = await api.file(props.directory, path);
      setFile({ path, content });
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function runSearch() {
    if (!search.trim()) return;
    try {
      setError(null);
      setSearchResults(await api.searchFiles(props.directory, search.trim()));
    } catch (e) {
      setError((e as Error).message);
    }
  }

  // Redimensionamento do painel
  function startResize(e: React.MouseEvent) {
    e.preventDefault();
    const startX = e.clientX;
    const startW = (e.currentTarget.parentElement as HTMLElement).getBoundingClientRect().width;
    const onMove = (ev: MouseEvent) => {
      const w = Math.min(Math.max(startW + (startX - ev.clientX), 300), window.innerWidth * 0.6);
      props.onWidthChange(w);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }

  const tabs: Array<{ id: TabId; label: string }> = [
    { id: "arquivos", label: "Arquivos" },
    { id: "git", label: "Git" },
    { id: "diffs", label: "Diffs" },
    { id: "tarefas", label: "Tarefas" },
    { id: "execucao", label: "Execução" },
  ];

  return (
    <aside className={cx("right-panel", props.collapsed && "collapsed")} aria-label="Painel do projeto">
      <div
        className="panel-resizer"
        onMouseDown={startResize}
        onDoubleClick={props.onToggleCollapse}
        title={props.collapsed ? "Duplo clique para restaurar o painel" : "Duplo clique para minimizar o painel"}
        aria-hidden
      />
      <div className="panel-tabs" role="tablist">
        {tabs.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            className={cx("panel-tab", tab === t.id && "active")}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="panel-body">
        {error ? <div className="error-banner">{error}</div> : null}

        {tab === "arquivos" ? (
          <div>
            <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
              <input
                className="input"
                style={{ flex: 1, minWidth: 140, padding: "6px 10px" }}
                placeholder="Buscar arquivos…"
                aria-label="Buscar arquivos no projeto"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && void runSearch()}
              />
              <button className="btn sm" onClick={() => void runSearch()}>
                Buscar
              </button>
            </div>

            {searchResults.length > 0 ? (
              <div style={{ marginBottom: 12 }}>
                <div className="side-label">Resultados</div>
                {searchResults.map((p) => (
                  <button key={p} className="tree-item" onClick={() => void openFile(p)}>
                    <span className="dim">📄</span> {p}
                  </button>
                ))}
              </div>
            ) : null}

            <div className="side-label">
              <span>
                {treePath ? (
                  <button className="btn ghost sm" onClick={() => setTreePath(treePath.split("/").slice(0, -1).join("/"))}>
                    ↑ voltar
                  </button>
                ) : (
                  "/"
                )}{" "}
                {treePath}
              </span>
              <button className="btn ghost sm" onClick={() => void loadTree()} title="Atualizar">
                ⟳
              </button>
            </div>

            {nodes.map((n) => (
              <button
                key={n.absolute}
                className={cx("tree-item", file?.path === n.absolute && "active")}
                onClick={() => {
                  if (n.type === "directory") setTreePath(n.absolute);
                  else void openFile(n.absolute);
                }}
              >
                <span className="dim">{n.type === "directory" ? "📁" : "📄"}</span>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{n.name}</span>
                {n.ignored ? <span className="chip" style={{ marginLeft: "auto" }}>ignorado</span> : null}
              </button>
            ))}

            {file ? (
              <div style={{ marginTop: 14 }}>
                <div className="side-label">
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: 11 }}>{file.path}</span>
                  <button className="btn ghost sm" onClick={() => setFile(null)}>
                    fechar
                  </button>
                </div>
                <div className="file-view">
                  {file.content.type === "binary" ? (
                    <div style={{ padding: 14 }}>Arquivo binário — visualização indisponível.</div>
                  ) : (
                    <pre>{file.content.content}</pre>
                  )}
                </div>
                {file.content.diff ? (
                  <>
                    <div className="side-label">Alterações não commitadas</div>
                    <div className="diff-file">
                      <DiffView patch={file.content.diff} />
                    </div>
                  </>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}

        {tab === "git" ? (
          <div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
              <span className="chip">branch: {gitInfo?.branch ?? "—"}</span>
              <span className="chip">padrão: {gitInfo?.default_branch ?? "—"}</span>
            </div>
            <div className="side-label">Alterações ({gitStatus.length})</div>
            {gitStatus.length === 0 ? (
              <p style={{ color: "var(--text-faint)", fontSize: 13 }}>Árvore de trabalho limpa.</p>
            ) : (
              gitStatus.map((f) => (
                <div key={f.file} className="tree-item" style={{ cursor: "default" }}>
                  <span
                    className={cx("chip", f.status === "added" ? "ok" : f.status === "deleted" ? "err" : "warn")}
                  >
                    {f.status}
                  </span>
                  <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>{f.file}</span>
                  <span className="chip ok">+{f.additions}</span>
                  <span className="chip err">−{f.deletions}</span>
                </div>
              ))
            )}
            <div className="side-label">Diff completo</div>
            <DiffFileList items={gitDiff} />
          </div>
        ) : null}

        {tab === "diffs" ? <DiffFileList items={sessionDiff} /> : null}

        {tab === "tarefas" ? (
          <div>
            {todos.length === 0 ? (
              <p style={{ color: "var(--text-faint)", fontSize: 13 }}>
                Sem tarefas registradas nesta sessão.
              </p>
            ) : (
              todos.map((t, i) => (
                <div key={i} className="tree-item" style={{ cursor: "default", alignItems: "flex-start" }}>
                  <span
                    className={cx(
                      "chip",
                      t.status === "completed" ? "ok" : t.status === "in_progress" ? "warn" : "",
                    )}
                  >
                    {t.status === "completed" ? "✓" : t.status === "in_progress" ? "◐" : "○"} {t.status}
                  </span>
                  <span style={{ flex: 1 }}>{t.content}</span>
                  <span className="chip">{t.priority}</span>
                </div>
              ))
            )}
          </div>
        ) : null}

        {tab === "execucao" ? (
          <div>
            <div className="side-label">Estado</div>
            <div className="tree-item" style={{ cursor: "default" }}>
              <span className={cx("chip", props.sessionStatus && props.sessionStatus.type !== "idle" ? "warn" : "ok")}>
                {props.sessionStatus?.type ?? "idle"}
              </span>
              <span style={{ flex: 1 }}>{props.sessionStatus?.message ?? "Sem execução ativa"}</span>
            </div>

            {props.session ? (
              <>
                <div className="side-label">Sessão</div>
                <div className="tree-item" style={{ cursor: "default", fontFamily: "var(--font-mono)", fontSize: 11 }}>
                  {props.session.id}
                </div>
                <div className="tree-item" style={{ cursor: "default" }}>
                  <span className="chip">custo {props.session.cost != null ? `US$ ${props.session.cost.toFixed(4)}` : "—"}</span>
                  <span className="chip">
                    tokens {((props.session.tokens?.input ?? 0) + (props.session.tokens?.output ?? 0)).toLocaleString("pt-BR")}
                  </span>
                </div>
              </>
            ) : null}

            <div className="side-label">Sessões filhas ({children.length})</div>
            {children.length === 0 ? (
              <p style={{ color: "var(--text-faint)", fontSize: 13 }}>Nenhuma sessão filha.</p>
            ) : (
              children.map((c) => (
                <button key={c.id} className="tree-item" onClick={() => props.onOpenSession(c.id)}>
                  <span className="dim">↳</span> {c.title || c.slug || c.id}
                </button>
              ))
            )}
          </div>
        ) : null}
      </div>
    </aside>
  );
}
